import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/lib/auth";
import ProtectedRoute from "@/components/ProtectedRoute";
import HorseList from "@/pages/HorseList";
import AdBuilder from "@/pages/AdBuilder";
import StyleLibrary from "@/pages/StyleLibrary";
import AdSettings from "@/pages/AdSettings";
import PriceList from "@/pages/PriceList";
import BidCenter from "@/pages/BidCenter";
import AuctionSimulator from "@/pages/AuctionSimulator";
import GroupLibrary from "@/pages/GroupLibrary";
import GroupOverview from "@/pages/GroupOverview";
import Login from "@/pages/Login";
import VerifyMagic from "@/pages/VerifyMagic";
import Dashboard from "@/pages/Dashboard";
import SellerSettings from "@/pages/SellerSettings";
import SellerListingEditor from "@/pages/SellerListingEditor";
import ReviewQueue from "@/pages/ReviewQueue";
import Pipeline from "@/pages/Pipeline";
import Contacts from "@/pages/Contacts";
import SellerPreviewPicker from "@/pages/SellerPreviewPicker";
import SellerWizard from "@/pages/SellerWizard";
import Apply from "@/pages/Apply";
import InviteLanding from "@/pages/InviteLanding";
import Applications from "@/pages/Applications";
import ApplicationDetail from "@/pages/ApplicationDetail";
import SellerApplication from "@/pages/SellerApplication";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CompanyManagement from "@/pages/CompanyManagement";
import CompanyPath from "@/pages/CompanyPath";
import PlatformHealth from "@/pages/PlatformHealth";
import FbLab from "@/pages/FbLab";
import { TitleSync } from "@/lib/pageTitle";
import { useBrand } from "@/lib/brand";
import { useAuth } from "@/lib/auth";
import { landing } from "@/lib/access";

const ADMIN = ["admin", "super_admin", "staff"];
const ANY = ["seller", "admin", "super_admin", "staff"];
const P = (areas, el) => <ProtectedRoute roles={ADMIN} areas={areas}>{el}</ProtectedRoute>;

function Home() {
  const { user } = useAuth();
  return <Navigate to={landing(user)} replace />;
}

function App() {
  useBrand(); // re-render once company branding (time zone, labels) has loaded
  return (
    <div className="App bahs-shell">
      <BrowserRouter>
        <TitleSync />
        <AuthProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/apply/:slug" element={<Apply />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/invite/:token" element={<InviteLanding />} />
            <Route path="/applications" element={P(["applications", "contracts"], <Applications />)} />
            <Route path="/applications/:id" element={P(["applications", "contracts", "pipeline"], <ApplicationDetail />)} />
            <Route path="/auth/verify" element={<VerifyMagic />} />

            {/* Seller */}
            <Route path="/dashboard" element={<ProtectedRoute roles={ANY}><Dashboard /></ProtectedRoute>} />
            <Route path="/dashboard/settings" element={<ProtectedRoute roles={ANY}><SellerSettings /></ProtectedRoute>} />
            <Route path="/dashboard/applications/:id" element={<ProtectedRoute roles={ANY}><SellerApplication /></ProtectedRoute>} />
            <Route path="/listing/:id" element={<ProtectedRoute roles={ANY}><SellerListingEditor /></ProtectedRoute>} />

            {/* Admin only */}
            <Route path="/" element={<ProtectedRoute roles={ADMIN}><Home /></ProtectedRoute>} />
            <Route path="/horses" element={P(["horse_ads"], <HorseList />)} />
            <Route path="/horse/:id" element={P(["horse_ads"], <AdBuilder />)} />
            <Route path="/review" element={P(["review"], <ReviewQueue />)} />
            <Route path="/pipeline" element={P(["pipeline"], <Pipeline />)} />
            <Route path="/contacts" element={P(["contacts"], <Contacts />)} />
            <Route path="/seller-preview" element={P(["horse_ads"], <SellerPreviewPicker />)} />
            <Route path="/seller-preview/:draftId" element={P(["horse_ads"], <SellerWizard />)} />
            <Route path="/style-library" element={P(["horse_ads"], <StyleLibrary />)} />
            <Route path="/settings" element={P(["settings"], <AdSettings />)} />
            <Route path="/price-list" element={P(["__admin_only"], <PriceList />)} />
            <Route path="/bids" element={P(["__admin_only"], <BidCenter />)} />
            <Route path="/bids/simulator" element={P(["__admin_only"], <AuctionSimulator />)} />
            <Route path="/groups" element={P(["__admin_only"], <GroupLibrary />)} />
            <Route path="/groups/overview" element={P(["__admin_only"], <GroupOverview />)} />
            <Route path="/platform/companies" element={<ProtectedRoute roles={["super_admin"]}><CompanyManagement /></ProtectedRoute>} />
            <Route path="/platform/health" element={<ProtectedRoute roles={["super_admin"]}><PlatformHealth /></ProtectedRoute>} />
            <Route path="/platform/fb-lab" element={<ProtectedRoute roles={["super_admin"]}><FbLab /></ProtectedRoute>} />
            <Route path="/c/:slug/*" element={<CompanyPath />} />
          </Routes>
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
